import { NavLink, useNavigate } from 'react-router-dom';
import { Stack, NavLink as MantineNavLink, Divider, Box } from '@mantine/core';
import {
  IconHome,
  IconBox,
  IconClipboardCheck,
  IconUsers,
  IconMapPin,
  IconChartBar,
  IconSettings,
  IconUser,
  IconFileAnalytics,
  IconDatabase,
  IconReportAnalytics,
  IconClockPlay,
  IconTemplate,
  IconHelp,
  IconHistory,
  IconBookmark,
  IconShare,
} from '@tabler/icons-react';
import { usePermissions, type PageName } from '@/hooks/usePermissions';
import { useOrganization } from '@/hooks/useOrganization';
import { useMenuViews, useSharedViews, type SavedFilter } from '@/hooks/api/useSavedFilters';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  to: string;
  badge?: string;
  page: PageName;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <IconHome size={20} />,
    to: '/dashboard',
    page: 'dashboard',
  },
  {
    label: 'Assets',
    icon: <IconBox size={20} />,
    to: '/assets',
    page: 'assets',
  },
  {
    label: 'Audits',
    icon: <IconClipboardCheck size={20} />,
    to: '/audits',
    page: 'audits',
  },
  {
    label: 'Activity Log',
    icon: <IconHistory size={20} />,
    to: '/activity-log',
    page: 'activity_log',
  },
];

const reportsItems: NavItem[] = [
  {
    label: 'Report Presets',
    icon: <IconTemplate size={20} />,
    to: '/reports/presets',
    page: 'reports',
  },
  {
    label: 'View Saved Reports',
    icon: <IconFileAnalytics size={20} />,
    to: '/reports/saved',
    page: 'reports',
  },
  {
    label: 'Create New Report',
    icon: <IconReportAnalytics size={20} />,
    to: '/reports/create',
    page: 'reports',
  },
  {
    label: 'Report Automations',
    icon: <IconClockPlay size={20} />,
    to: '/reports/automations',
    page: 'reports',
  },
];

const settingsItems: NavItem[] = [
  {
    label: 'Account & Billing',
    icon: <IconSettings size={20} />,
    to: '/settings',
    page: 'settings',
  },
  {
    label: 'People',
    icon: <IconUsers size={20} />,
    to: '/settings/people',
    page: 'people',
  },
  {
    label: 'Locations',
    icon: <IconMapPin size={20} />,
    to: '/settings/locations',
    page: 'locations',
  },
  {
    label: 'Categories',
    icon: <IconSettings size={20} />,
    to: '/settings/categories',
    page: 'categories',
  },
  {
    label: 'Departments',
    icon: <IconSettings size={20} />,
    to: '/settings/departments',
    page: 'departments',
  },
  {
    label: 'Fields',
    icon: <IconDatabase size={20} />,
    to: '/settings/fields',
    page: 'settings',
  },
  {
    label: 'Custom Fields',
    icon: <IconSettings size={20} />,
    to: '/settings/custom-fields',
    page: 'custom_fields',
  },
  {
    label: 'Users',
    icon: <IconUser size={20} />,
    to: '/settings/users',
    page: 'settings',
  },
  {
    label: 'Roles',
    icon: <IconSettings size={20} />,
    to: '/settings/roles',
    page: 'settings',
  },
];

interface NavbarProps {
  onMobileNavClick?: () => void;
}

const entityRoutes: Record<string, string> = {
  assets: '/assets',
  audits: '/audits',
  users: '/settings/users',
};

export function Navbar({ onMobileNavClick }: NavbarProps) {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const { organizationId } = useOrganization();

  // Fetch saved views for menu
  const { data: menuViews = [] } = useMenuViews(organizationId || '');
  const { data: sharedViews = [] } = useSharedViews(organizationId || '');

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => canAccess(item.page));
  const visibleReportsItems = reportsItems.filter((item) => canAccess(item.page));
  const visibleSettingsItems = settingsItems.filter((item) => canAccess(item.page));

  // Handle saved view click
  const handleViewClick = (view: SavedFilter) => {
    const baseRoute = entityRoutes[view.entity_type] || '/assets';
    const filterParam = encodeURIComponent(JSON.stringify(view.filter_config));
    navigate(`${baseRoute}?savedFilter=${view.id}&filterConfig=${filterParam}`);
    onMobileNavClick?.();
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Main navigation - scrollable */}
      <Box style={{ flex: 1, overflowY: 'auto' }}>
        <Stack gap="xs">
          {visibleNavItems.map((item) => (
            <MantineNavLink
              key={item.to}
              component={NavLink}
              to={item.to}
              label={item.label}
              leftSection={item.icon}
              onClick={onMobileNavClick}
              styles={{
                root: {
                  borderRadius: '8px',
                },
              }}
            />
          ))}

          {/* Favorite Views */}
          {menuViews.length > 0 && (
            <MantineNavLink
              label="Favorite Views"
              leftSection={<IconBookmark size={20} />}
              childrenOffset={28}
              defaultOpened={false}
              styles={{
                root: {
                  borderRadius: '8px',
                },
              }}
            >
              {menuViews.map((view) => (
                <MantineNavLink
                  key={view.id}
                  label={view.name}
                  description={view.entity_type}
                  onClick={() => handleViewClick(view)}
                  styles={{
                    root: {
                      borderRadius: '8px',
                    },
                  }}
                />
              ))}
            </MantineNavLink>
          )}

          {/* Shared Views */}
          {sharedViews.length > 0 && (
            <MantineNavLink
              label="Shared Views"
              leftSection={<IconShare size={20} />}
              childrenOffset={28}
              defaultOpened={false}
              styles={{
                root: {
                  borderRadius: '8px',
                },
              }}
            >
              {sharedViews.map((view) => (
                <MantineNavLink
                  key={view.id}
                  label={view.name}
                  description={view.entity_type}
                  onClick={() => handleViewClick(view)}
                  styles={{
                    root: {
                      borderRadius: '8px',
                    },
                  }}
                />
              ))}
            </MantineNavLink>
          )}

          {visibleReportsItems.length > 0 && (
            <MantineNavLink
              label="Custom Reporting"
              leftSection={<IconChartBar size={20} />}
              childrenOffset={28}
              defaultOpened={false}
              styles={{
                root: {
                  borderRadius: '8px',
                },
              }}
            >
              {visibleReportsItems.map((item) => (
                <MantineNavLink
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  label={item.label}
                  onClick={onMobileNavClick}
                  styles={{
                    root: {
                      borderRadius: '8px',
                    },
                  }}
                />
              ))}
            </MantineNavLink>
          )}

          {visibleSettingsItems.length > 0 && (
            <MantineNavLink
              label="Settings"
              leftSection={<IconSettings size={20} />}
              childrenOffset={28}
              defaultOpened={false}
              styles={{
                root: {
                  borderRadius: '8px',
                },
              }}
            >
              {visibleSettingsItems.map((item) => (
                <MantineNavLink
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === '/settings'}
                  label={item.label}
                  onClick={onMobileNavClick}
                  styles={{
                    root: {
                      borderRadius: '8px',
                    },
                  }}
                />
              ))}
            </MantineNavLink>
          )}
        </Stack>
      </Box>

      {/* Footer section - always visible at bottom */}
      <Box mt="auto" pt="md">
        <Divider mb="sm" />
        <Stack gap="xs">
          <MantineNavLink
            component={NavLink}
            to="/help"
            label="Help & Support"
            description="Guides, FAQ & contact"
            leftSection={<IconHelp size={20} />}
            onClick={onMobileNavClick}
            styles={{
              root: {
                borderRadius: '8px',
              },
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
